import React from "react";
import { GLYPH_ADVANCE, GLYPH_UNITS_PER_EM, layoutGlyphs } from "@mockd/shared/brand";

type LogoVariant = "full" | "short";
type LogoSize = "sm" | "md" | "lg" | "xl";

interface MockdLogoProps {
  variant?: LogoVariant;
  size?: LogoSize;
  className?: string;
  accentColor?: boolean;
}

const sizes: Record<LogoVariant, Record<LogoSize, { width: number; height: number; fontSize: number }>> = {
  full: {
    sm: { width: 120, height: 32, fontSize: 20 },
    md: { width: 180, height: 48, fontSize: 30 },
    lg: { width: 240, height: 64, fontSize: 40 },
    xl: { width: 320, height: 80, fontSize: 52 },
  },
  short: {
    sm: { width: 32, height: 32, fontSize: 20 },
    md: { width: 48, height: 48, fontSize: 30 },
    lg: { width: 64, height: 64, fontSize: 40 },
    xl: { width: 80, height: 80, fontSize: 52 },
  },
};

export function MockdLogo({
  variant = "full",
  size = "md",
  className = "",
  accentColor = true,
}: MockdLogoProps) {
  const { width, height, fontSize } = sizes[variant][size];
  const glyphs = layoutGlyphs(variant === "full" ? "mockd_" : "m_");
  // Outlined glyphs, laid out where centred <text> at this font size put them,
  // so the logo is the same before, after, or without the web font.
  const scale = fontSize / GLYPH_UNITS_PER_EM;
  const x = width / 2 - (glyphs.length * GLYPH_ADVANCE * scale) / 2;
  const baseline = height * 0.68;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className={className}
      role="img"
      aria-label="mockd"
    >
      <g transform={`translate(${x} ${baseline}) scale(${scale})`}>
        {glyphs.map(({ char, d, x: glyphX }) => (
          <path
            key={glyphX}
            d={d}
            transform={`translate(${glyphX} 0)`}
            className={char === "_" && accentColor ? "fill-primary" : "fill-base-content"}
          />
        ))}
      </g>
    </svg>
  );
}
